// InvoiceRunsPage — 請求書処理run (#invoice_runs, 2026-07-07)
//
// 「前回の請求書処理で最後に分析した請求書から後の分を、◯月◯日支払分として一括処理」
// の口頭日付指定を廃し、カーソル起点ワンクリック運用にする画面。
//   - 最上部: 新規処理カード (前回カーソル + 未分析プレビュー + 除外トグル + 支払日 + 二段階インライン確認)
//     * 開始ガード: open/attention の run がある間はカード全体をグレーアウトし進捗バナー表示
//       (「◯/◯完了」+ 詳細を見る)。プレビュー取得も停止 (無駄なARICA走査防止)、run close で自動再開
//   - 下部: run 台帳 (「抽出 n/N・proposal化 n/N・あなたの対応待ち k・除外 j」カウンタ。処理中は10秒自動更新)
//     * run 詳細: 「いまの状況」ストリップ (処理中/あなたの対応待ち/完了/除外 の統一語彙 — lib/statusVocab.ts)
//       + note の機械語は lib/reviewReasons.ts で平易な日本語+推奨アクションに翻訳 (生noteは折りたたみ)
//       + item単位の行内アクション: 再作成 (rebuild-blocked rowids) / 再実行 (redispatch rowids) / AIに指示 (reprocess_invoice)
//       + [請求書を見る] = 行直下にインライン展開ビューア (components/PdfViewer 再利用 = 仕訳検証ページと同一挙動。
//         下方向展開なので右下の指示チャット(CommandDock)と非干渉。同時1件・再/別行クリックで閉じ切替・新規タブ↗併設)
//   - run 完了(close) でカーソルが to_rowid へ前進 → 次回はそこから
//   - run 完了後の動線: proposal化済みの件は 請求書処理(#invoice) で承認 → 一括登録(バッチ台帳 #batches で照合)
// デザインは BatchesPage のパレットC準拠 (navy #203a57・罫線・monospace数字)。
// worker API は並行実装中 — 未デプロイ時はエラー表示のみでレイアウトは崩さない。

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Container, Button, InlineSpinner, MessageBlock } from '@freee_jp/vibes';
import { MdRefresh, MdCheckCircle, MdWarningAmber, MdOutlineFlag, MdPlayArrow } from 'react-icons/md';
import {
  fetchInvoiceRunPreview, createInvoiceRun, fetchInvoiceRuns, fetchInvoiceRunDetail,
  closeInvoiceRun, redispatchInvoiceRunFailed, rebuildInvoiceRunBlocked, fetchJobs,
  createJob, invoicePdfUrl, resolveInvoiceRunItem, fetchSectionsMap,
  InvoiceRunPreview, InvoiceRunSummary, InvoiceRunItem,
} from '../api/client';
import { runItemToUnified, runStatusToUnified } from '../lib/statusVocab';
import { translateNote, groupReasons, ReviewReason } from '../lib/reviewReasons';
import { PdfViewer } from '../components/PdfViewer';

// ---------------------------------------------------------------------------- パレットC
const C = {
  ground: '#edf1f5', ink: '#1d2a3a', sub: '#647386', line: '#d9e0e7', lineDark: '#bdc8d3',
  navy: '#203a57', good: '#267a62', goodBg: '#edf8f4', warn: '#a46a12', warnBg: '#fdf3e3',
  danger: '#b3261e', dangerBg: '#fdecea',
  blue: '#33699e', blueBg: '#f0f6ff', blueBd: '#b9d2ed',
};
const NUM = '"SFMono-Regular",Consolas,Menlo,monospace';
const FONT = '"Hiragino Sans","Yu Gothic UI","Meiryo",sans-serif';

const S = {
  page: { background: C.ground, minHeight: '100vh', paddingBottom: '3rem', color: C.ink, fontFamily: FONT, fontSize: 14 } as React.CSSProperties,
  sheet: { border: '1px solid #c8d1db', borderRadius: 10, background: '#fff', boxShadow: '0 9px 22px rgba(33,52,74,.08)', marginTop: 16, overflow: 'hidden' } as React.CSSProperties,
  top: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, minHeight: 58, padding: '8px 18px 8px 20px', color: '#fff', background: C.navy, flexWrap: 'wrap' } as React.CSSProperties,
  topTitle: { margin: 0, fontSize: 19, letterSpacing: '.025em', fontWeight: 700 } as React.CSSProperties,
  topCaption: { color: '#c9d6e5', fontSize: 12, fontWeight: 600 } as React.CSSProperties,
  pill: { display: 'inline-flex', alignItems: 'center', gap: 5, padding: '4px 10px', border: '1px solid rgba(255,255,255,.35)', borderRadius: 13, background: 'rgba(255,255,255,.12)', color: '#e5eef8', fontSize: 11, fontWeight: 800, whiteSpace: 'nowrap' } as React.CSSProperties,
  list: { padding: '14px 18px 18px', background: '#f7f9fb' } as React.CSSProperties,
  // attention枠 = 橙 (あなたの番)。赤枠は使わない (赤=失敗事実のみ — 統一色規則)
  card: (attention: boolean): React.CSSProperties => ({ marginBottom: 12, border: `1px solid ${attention ? '#e3c68a' : C.lineDark}`, borderRadius: 7, background: '#fff', overflow: 'hidden' }),
  cardHead: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, padding: '12px 15px', flexWrap: 'wrap', cursor: 'pointer' } as React.CSSProperties,
  counter: (color: string): React.CSSProperties => ({ fontFamily: NUM, fontSize: 16, fontWeight: 900, color, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }),
  badge: (bg: string, fg: string, bd: string): React.CSSProperties => ({ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 8px', borderRadius: 3, fontSize: 10, fontWeight: 800, background: bg, color: fg, border: `1px solid ${bd}`, whiteSpace: 'nowrap' }),
  th: { textAlign: 'left', padding: '7px 10px', borderBottom: `1px solid ${C.lineDark}`, fontSize: 10, color: C.sub, fontWeight: 800, letterSpacing: '.05em', background: '#f4f7fa', whiteSpace: 'nowrap' } as React.CSSProperties,
  td: { padding: '7px 10px', borderBottom: `1px solid ${C.line}`, fontSize: 12, verticalAlign: 'top' } as React.CSSProperties,
  flowHint: { display: 'flex', alignItems: 'center', gap: 6, padding: '8px 12px', borderRadius: 5, background: '#f0f6ff', border: '1px solid #b9d2ed', color: '#33699e', fontSize: 12, fontWeight: 700, flexWrap: 'wrap' } as React.CSSProperties,
};

// ---------------------------------------------------------------------------- 小物
const fmtTs = (t: number | string | null | undefined) => {
  if (!t) return '—';
  const d = new Date(typeof t === 'string' && /^\d+$/.test(t) ? Number(t) : t);
  if (isNaN(d.getTime())) return String(t);
  const p = (x: number) => String(x).padStart(2, '0');
  return `${d.getFullYear()}/${p(d.getMonth() + 1)}/${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
};
const fmtYen = (n: number | null | undefined) => (n == null ? '—' : `¥${Number(n).toLocaleString('ja-JP')}`);
const fmtDateJp = (iso: string) => {
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return m ? `${Number(m[2])}月${Number(m[3])}日` : iso;
};
// 既定支払日 = 次の25日 (今日が25日以前なら当月25日、26日以降なら翌月25日)
const next25th = (): string => {
  const d = new Date();
  const base = d.getDate() <= 25 ? new Date(d.getFullYear(), d.getMonth(), 25) : new Date(d.getFullYear(), d.getMonth() + 1, 25);
  const p = (x: number) => String(x).padStart(2, '0');
  return `${base.getFullYear()}-${p(base.getMonth() + 1)}-25`;
};

// 小型スピナー (badge/バナー内用。vibes InlineSpinner は badge には大きすぎるため)
const MiniSpin: React.FC<{ size?: number }> = ({ size = 11 }) => (
  <span aria-hidden style={{
    width: size, height: size, flex: '0 0 auto', boxSizing: 'border-box',
    border: `2px solid ${C.blueBd}`, borderTopColor: C.blue, borderRadius: '50%',
    display: 'inline-block', animation: 'irspin .8s linear infinite',
  }} />
);

// item state 分類 (worker 並行実装のため別名も許容)
const PROPOSED_STATES = new Set(['proposed', 'proposal_done', 'succeeded']);
const REVIEW_STATES = new Set(['needs_review', 'blocked', 'review']);
const FAILED_STATES = new Set(['failed', 'error', 'dead_letter']);
const EXCLUDED_STATES = new Set(['excluded', 'skipped']);

// item state → 統一語彙バッジ (lib/statusVocab.ts に集約。旧 STATE_LABEL は 2026-07-08 に全廃)
// dp の生存判定: needs_review でも proposal が duplicate系/rejected で生きている場合は重複判定画面へ誘導する
const LIVE_DP_STATUSES = new Set(['duplicate_review', 'duplicate_rejected', 'rejected']);

// run status → 統一語彙 (lib/statusVocab.ts runStatusToUnified)。
//   attention = run内に対応待ちitemが残存 (runの失敗ではない) → 橙「対応待ちあり」+ 件数内訳を併記
const RUN_STATUS: Record<string, [string, string, string, string]> = Object.fromEntries(
  ['open', 'attention', 'closed'].map((s) => {
    const u = runStatusToUnified(s);
    return [s, [u.label, u.bg, u.fg, u.bd]];
  }),
) as Record<string, [string, string, string, string]>;

// counts_json を防御的に集計 (worker 並行実装 — キー名の揺れを吸収)
// done = proposed のみ (2026-07-08: 「◯/◯完了」に needs_review が埋まる問題の修正。
//   needs_review・失敗・開始失敗は actionRequired = あなたの対応待ち として別掲する)
interface RunCounts { n: number; extractDone: number; proposed: number; needsReview: number; excluded: number; failed: number; done: number; actionRequired: number; active: boolean }
const countsOf = (r: InvoiceRunSummary): RunCounts => {
  let raw: Record<string, any> = {};
  try { const o = JSON.parse(r.counts_json || '{}'); if (o && typeof o === 'object') raw = o; } catch { /* 破損JSONは空扱い */ }
  const g = (...keys: string[]) => keys.reduce((s, k) => s + (Number(raw[k]) || 0), 0);
  const excluded = g('excluded', 'skipped');
  const failed = g('failed', 'error', 'dead_letter');
  const needsReview = g('needs_review', 'blocked', 'review');
  const proposed = g('proposed', 'proposal_done', 'proposal', 'succeeded', 'done');
  const extractDone = g('extracted', 'extract_done') + proposed + needsReview;
  const pendingish = g('pending', 'queued', 'waiting', 'dispatched', 'extracting', 'running', 'claimed');
  const totalRaw = Object.values(raw).reduce((s: number, v: any) => s + (Number(v) || 0), 0);
  const n = Math.max((typeof r.total_count === 'number' ? r.total_count : totalRaw) - excluded, 0);
  return { n, extractDone, proposed, needsReview, excluded, failed, done: proposed, actionRequired: needsReview + failed + g('pending'), active: pendingish > 0 };
};

const isActiveRunStatus = (s: string) => s === 'open' || s === 'attention';

const readHashId = (): string | null => {
  const m = window.location.hash.match(/^#?invoice_runs\/([^/]+)/);
  return m ? decodeURIComponent(m[1]) : null;
};

// ---------------------------------------------------------------------------- 本体
export const InvoiceRunsPage: React.FC = () => {
  // -- 新規処理カード --
  const [preview, setPreview] = useState<InvoiceRunPreview | null>(null);
  const [previewErr, setPreviewErr] = useState<string | null>(null);
  const [excluded, setExcluded] = useState<Set<number>>(new Set());
  const [payDate, setPayDate] = useState<string>(next25th());
  const [confirming, setConfirming] = useState(false);
  const [creating, setCreating] = useState(false);
  // -- run 台帳 --
  const [runs, setRuns] = useState<InvoiceRunSummary[]>([]);
  const [runsErr, setRunsErr] = useState<string | null>(null);
  const [detailId, setDetailId] = useState<string | null>(() => readHashId());
  const [detail, setDetail] = useState<{ run: InvoiceRunSummary; items: InvoiceRunItem[] } | null>(null);
  // items に job_status が無い場合の fallback: jobs API (thread_id=run_id) で claimed/running の job_id 集合
  const [activeJobIds, setActiveJobIds] = useState<Set<string>>(new Set());
  const [jobsProbeFailed, setJobsProbeFailed] = useState(false);
  const [loading, setLoading] = useState(false);
  // -- 操作系 --
  const [actionErr, setActionErr] = useState<string | null>(null);
  const [closingId, setClosingId] = useState<string | null>(null);
  const [closeReason, setCloseReason] = useState('');
  const [rebuildingId, setRebuildingId] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const loadPreview = useCallback(async () => {
    setPreviewErr(null);
    try {
      const r = await fetchInvoiceRunPreview();
      setPreview(r);
      // 既定 = 除外候補はチェックON (=除外)。手動トグルはプレビュー再取得でリセット
      setExcluded(new Set((r.items || []).filter((i) => !!i.excluded_suggested).map((i) => i.rowid)));
    } catch (e: unknown) {
      setPreview(null);
      setPreviewErr(`プレビュー取得失敗: ${e instanceof Error ? e.message : String(e)}`);
    }
  }, []);

  const loadDetail = useCallback(async (id: string) => {
    const d = await fetchInvoiceRunDetail(id);
    const items = d.items || [];
    const jobIds = new Set<string>();
    // items に job_status が含まれない worker 実装向け fallback:
    // live_state==='dispatched' (別名 state==='dispatched') の item があれば jobs API を thread_id={run_id} で引き、
    // claimed/running の job_id と突合して「今どれを処理中か」を出す
    const hasJobStatus = items.some((it) => it.job_status != null);
    if (!hasJobStatus && items.some((it) => (it.live_state === 'dispatched' || it.state === 'dispatched') && it.job_id)) {
      try {
        const jr = await fetchJobs({ thread_id: String(d.run.id), limit: 300 });
        for (const j of jr.jobs || []) {
          if (j.status === 'claimed' || j.status === 'running') jobIds.add(j.id);
        }
        setJobsProbeFailed(false);
      } catch { setJobsProbeFailed(true); /* jobs API 未対応でも表は出す (突合なし) */ }
    }
    setDetail(d);
    setActiveJobIds(jobIds);
  }, []);

  const loadRuns = useCallback(async (): Promise<InvoiceRunSummary[] | null> => {
    setRunsErr(null);
    try {
      const r = await fetchInvoiceRuns();
      setRuns(r.runs || []);
      const id = readHashId();
      setDetailId(id);
      if (id) await loadDetail(id);
      else { setDetail(null); setActiveJobIds(new Set()); }
      return r.runs || [];
    } catch (e: unknown) {
      setRunsErr(`run一覧取得失敗: ${e instanceof Error ? e.message : String(e)}`);
      return null;
    }
  }, [loadDetail]);

  // run一覧を先に見て、処理中 run が無いときだけプレビューを取る (グレーアウト中の無駄なARICA走査防止)
  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const fetched = await loadRuns();
      const hasActive = (fetched || []).some((r) => isActiveRunStatus(r.status));
      if (!hasActive) await loadPreview();
    } finally { setLoading(false); }
  }, [loadRuns, loadPreview]);

  useEffect(() => { loadAll(); }, [loadAll]);
  useEffect(() => {
    const onHash = () => { setDetailId(readHashId()); loadRuns(); };
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, [loadRuns]);
  const [instructRowid, setInstructRowid] = useState<number | null>(null); // InstructBox を開いている行 (前方宣言: 下のpolling効果が参照)
  const [viewerRowid, setViewerRowid] = useState<number | null>(null);      // インラインビューアを開いている行
  const [sectionPickRowid, setSectionPickRowid] = useState<number | null>(null); // 部門ピッカーを開いている行 (同時1件)
  // 処理中 run (open/attention) がある間は 10 秒毎に自動更新 (プレビューは停止 — run close 時に自動再開)
  // ただしインラインビューア/指示欄/部門ピッカーが開いている間は一時停止 —
  // 再renderで展開行が作り直され PdfViewer が probing に巻き戻り続ける実バグの対策 (2026-07-08 実UIデバッグ)
  // (部門ピッカーも同様: 選択中に行が並べ替わると誤クリックの元)
  useEffect(() => {
    if (viewerRowid !== null || instructRowid !== null || sectionPickRowid !== null) return;
    const hasActive = runs.some((r) => isActiveRunStatus(r.status));
    if (!hasActive && !detailId) return;
    const t = setInterval(loadRuns, 10000);
    return () => clearInterval(t);
  }, [runs, detailId, loadRuns, viewerRowid, instructRowid, sectionPickRowid]);
  // 処理中 run が「有 → 無」に転じたらプレビュー取得を自動再開 (close でカーソルが前進しているため)
  const activeRuns = useMemo(() => runs.filter((r) => isActiveRunStatus(r.status)), [runs]);
  const activeRun = activeRuns.length > 0 ? activeRuns[0] : null;
  const guardActive = !!activeRun;
  const prevGuardRef = useRef<boolean | null>(null);
  useEffect(() => {
    if (prevGuardRef.current === true && !guardActive) loadPreview();
    prevGuardRef.current = guardActive;
  }, [guardActive, loadPreview]);
  // toast は 8 秒で自動消滅
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 8000);
    return () => clearTimeout(t);
  }, [toast]);

  // -- 新規 run 開始 (二段階インライン確認の2段目から呼ぶ) --
  const includedCount = useMemo(() => (preview ? (preview.items || []).filter((i) => !excluded.has(i.rowid)).length : 0), [preview, excluded]);
  // 未分析が 1 件以上あるのに全件除外 = 抽出対象 0 件。「除外のみ確定してカーソル前進」モード
  const skipOnly = !!preview && (preview.items || []).length > 0 && includedCount === 0;
  const doCreate = useCallback(async () => {
    if (!preview) return;
    setCreating(true); setActionErr(null);
    try {
      const ex = (preview.items || []).filter((i) => excluded.has(i.rowid)).map((i) => i.rowid);
      const r = await createInvoiceRun(payDate, ex);
      setConfirming(false);
      setPreview(null); setExcluded(new Set());
      if (r.skip_only) {
        // 全件除外 = 抽出対象なし。worker 側で run は即 close 済みなので詳細へ飛ばさず
        // 一覧+プレビューを取り直す (前進したカーソルの先の未分析が出る)
        setToast(`${r.excluded ?? ex.length}件を除外してカーソルを rowid ${r.cursor ?? '—'} に前進しました (run ${r.run_id})`);
        await loadAll();
        return;
      }
      setToast(`run ${r.run_id} を開始しました (対象 ${r.dispatched ?? r.total}件・除外 ${r.excluded ?? ex.length}件)`);
      // 開始直後は開始ガードが立つのでプレビューは取り直さない (stale表示防止のため破棄)
      window.location.hash = `#invoice_runs/${r.run_id}`; // hashchange → loadRuns
    } catch (e: unknown) {
      setActionErr(`run開始失敗: ${e instanceof Error ? e.message : String(e)}`);
    } finally { setCreating(false); }
  }, [preview, excluded, payDate, loadAll]);

  // -- run close (カーソル前進) --
  const doClose = useCallback(async (run: InvoiceRunSummary) => {
    setActionErr(null);
    try {
      const r = await closeInvoiceRun(run.id, closeReason.trim() || undefined);
      const cur = (r as any)?.cursor ?? (r as any)?.new_cursor ?? run.to_rowid;
      setToast(`カーソルが rowid ${cur ?? '—'} に前進しました`);
      setClosingId(null); setCloseReason('');
      // close で処理中 run が無くなればガード解除 effect がプレビューを自動再取得する
      await loadRuns();
    } catch (e: unknown) {
      setActionErr(`close失敗: ${e instanceof Error ? e.message : String(e)}`);
    }
  }, [closeReason, loadRuns]);

  const doRedispatch = useCallback(async (id: string | number) => {
    setActionErr(null);
    try {
      const r = await redispatchInvoiceRunFailed(id, { include_pending: true });
      const nOk = Number(r.retried || 0) + Number(r.reissued || 0) + Number(r.redispatched || 0);
      setToast(`失敗・未開始分の再実行を依頼しました (${nOk}件)`);
      await loadRuns();
    } catch (e: unknown) {
      setActionErr(`再実行依頼失敗: ${e instanceof Error ? e.message : String(e)}`);
    }
  }, [loadRuns]);

  // -- エラー対応分のまとめて再作成 (2026-07-08: E4遵守のため対象rowidsを明示して渡す。
  //    rowids無し呼出だと server が amount_mismatch/GATE_D 等の再試行禁止分まで再buildするため) --
  const doRebuild = useCallback(async (id: string | number, rowids: number[]) => {
    if (!rowids.length) return;
    setActionErr(null); setRebuildingId(String(id));
    try {
      const r = await rebuildInvoiceRunBlocked(id, { rowids });
      const nOk = Number(r.rebuilt ?? r.resolved ?? r.success ?? r.succeeded ?? 0);
      const nNg = Number(r.unresolved ?? r.still_blocked ?? r.unresolvable ?? r.failed ?? 0);
      const nRe = Number(r.re_extracted ?? 0);
      setToast(`再作成: 成功${nOk}・再読取${nRe}・解決できず${nNg}`);
      await loadRuns();
    } catch (e: unknown) {
      setActionErr(`再作成失敗: ${e instanceof Error ? e.message : String(e)}`);
    } finally { setRebuildingId(null); }
  }, [loadRuns]);

  const toggleExclude = (rowid: number) => setExcluded((s) => {
    const n = new Set(s);
    if (n.has(rowid)) n.delete(rowid); else n.add(rowid);
    return n;
  });

  // -- 「今まさに抽出中」判定 (job_status 優先、無ければ jobs API 突合、旧worker向けに state 別名も) --
  const isActiveItem = useCallback((it: InvoiceRunItem): boolean => (
    it.job_status === 'claimed' || it.job_status === 'running'
    || (it.job_id != null && activeJobIds.has(String(it.job_id)))
    || it.state === 'extracting' || it.state === 'running' || it.state === 'claimed'
  ), [activeJobIds]);

  // -- run 詳細の「◯/◯完了」集計 (items 実データから。counts_json より正確) --
  //   done = proposed のみ (2026-07-08)。actionRequired = review + failed + pending。
  //   dupReview = dp が重複判定/却下で生きている件 (着地は #duplicate_candidates)。
  const detailAgg = useMemo(() => {
    if (!detail) return null;
    const items = detail.items || [];
    let proposed = 0, review = 0, failed = 0, excludedN = 0, activeN = 0, pendingN = 0, dupReview = 0;
    for (const it of items) {
      if (PROPOSED_STATES.has(it.state)) proposed += 1;
      else if (REVIEW_STATES.has(it.state)) {
        review += 1;
        if (LIVE_DP_STATUSES.has(String(it.proposal_status || ''))) dupReview += 1;
      }
      else if (FAILED_STATES.has(it.state)) failed += 1;
      else if (EXCLUDED_STATES.has(it.state)) excludedN += 1;
      else if (it.state === 'pending') pendingN += 1;
      if (isActiveItem(it)) activeN += 1;
    }
    const n = Math.max(items.length - excludedN, 0);
    const actionRequired = review + failed + pendingN;
    return { n, proposed, review, failed, activeN, done: proposed, actionRequired, dupReview, processing: Math.max(n - proposed - actionRequired, 0) };
  }, [detail, isActiveItem]);

  // -- 行内アクション (2026-07-08: item単位の再作成/再実行/AIへの指示) --
  const [itemBusy, setItemBusy] = useState<number | null>(null);
  const [itemMsg, setItemMsg] = useState<Record<number, string>>({});
  const [sentMap, setSentMap] = useState<Record<number, string>>({});      // rowid → 指示送信時刻
  const [instructText, setInstructText] = useState('');
  const [reasonFilter, setReasonFilter] = useState<string | null>(null);   // groupReasons チップの絞り込み
  // 行下展開のインライン請求書ビューア (仕訳検証ページと同じ components/PdfViewer を再利用。
  //   下方向展開 = 画面右下の指示チャット(CommandDock)と物理的に非干渉。同時に開けるのは1件のみ)
  useEffect(() => { setViewerRowid(null); setSectionPickRowid(null); }, [detailId]); // run 切替時は閉じる
  // 展開直後にビューア行を画面内へスクロール (実バグ対策 2026-07-08):
  //   PdfViewer 内部の @react-pdf-viewer/core Viewer は IntersectionObserver で「コンテナが
  //   viewport に入って初めて」PDF 本体の取得・描画を開始する (lazy load)。画面下端の行で
  //   [請求書を見る] を押すと展開行が fold の下に生まれ、スクロールするまで永遠に空白に見える。
  //   block:'nearest' なので既に見えている場合は何もしない (JournalVerificationPage には影響なし)。
  useEffect(() => {
    if (viewerRowid === null) return;
    document.getElementById(`invoice-viewer-row-${viewerRowid}`)?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [viewerRowid]);

  const doRebuildItem = useCallback(async (runId: string | number, rowid: number) => {
    setActionErr(null); setItemBusy(rowid);
    try {
      const r = await rebuildInvoiceRunBlocked(runId, { rowids: [rowid] });
      const one = (r.details || []).find((d) => Number(d.rowid) === rowid);
      const outcome = String(one?.outcome || (Number(r.rebuilt) > 0 ? 'rebuilt' : 'unresolved'));
      setItemMsg((m) => ({
        ...m,
        [rowid]: outcome === 'rebuilt' ? '仕訳を作成しました — 承認へ進めます'
          : outcome.startsWith('re_extract') ? '再読取を開始しました (数分お待ちください)'
          : `解決できませんでした (${one?.reason || outcome})`,
      }));
      await loadRuns();
    } catch (e: unknown) {
      setActionErr(`再作成失敗: ${e instanceof Error ? e.message : String(e)}`);
    } finally { setItemBusy(null); }
  }, [loadRuns]);

  const doRedispatchItem = useCallback(async (runId: string | number, rowid: number) => {
    setActionErr(null); setItemBusy(rowid);
    try {
      const r = await redispatchInvoiceRunFailed(runId, { rowids: [rowid], include_pending: true });
      const nOk = Number(r.retried || 0) + Number(r.reissued || 0);
      setItemMsg((m) => ({ ...m, [rowid]: nOk > 0 ? '再実行を開始しました' : '再実行できませんでした' }));
      await loadRuns();
    } catch (e: unknown) {
      setActionErr(`再実行失敗: ${e instanceof Error ? e.message : String(e)}`);
    } finally { setItemBusy(null); }
  }, [loadRuns]);

  // -- [部門を指定して再作成] (2026-07-08): section_unresolved 系を Claude 経由なしの数秒操作で解消 --
  //   freee 部門 canonical 一覧 = client.ts fetchSectionsMap() (freee-proxy /api/1/sections。定数化禁止) を流用。
  //   単一選択 = 全明細行に同一部門を適用し POST /resolve-item → 成功で行は「完了(→承認へ)」へ。
  const [sectionOptions, setSectionOptions] = useState<Array<{ id: number; name: string }> | null>(null);
  const [sectionOptionsErr, setSectionOptionsErr] = useState<string | null>(null);
  const [pickedSection, setPickedSection] = useState<number | null>(null);
  const [resolveErr, setResolveErr] = useState<Record<number, string>>({});   // rowid → resolve失敗理由 (ピッカー内に表示)
  const [resolvedRowids, setResolvedRowids] = useState<Set<number>>(new Set()); // resolve成功の楽観表示 (refresh遅延の橋渡し)

  const openSectionPicker = useCallback((rowid: number) => {
    setSectionPickRowid((cur) => (cur === rowid ? null : rowid));
    setPickedSection(null);
    setSectionOptionsErr(null);
    if (!sectionOptions) {
      fetchSectionsMap()
        .then((m) => setSectionOptions(Array.from(m, ([sid, name]) => ({ id: sid, name }))))
        .catch((e: unknown) => setSectionOptionsErr(`部門一覧の取得に失敗: ${e instanceof Error ? e.message : String(e)}`));
    }
  }, [sectionOptions]);

  const doResolveSection = useCallback(async (runId: string | number, it: InvoiceRunItem, sectionId: number, sectionName: string) => {
    setActionErr(null); setItemBusy(it.rowid);
    setResolveErr((m) => { const n = { ...m }; delete n[it.rowid]; return n; });
    try {
      const r = await resolveInvoiceRunItem(runId, it.rowid, [sectionId]);
      if (r?.ok === false || r?.error) throw new Error(String(r?.error || r?.reason || 'resolve-item が失敗を返しました'));
      setResolvedRowids((s) => { const n = new Set(s); n.add(it.rowid); return n; });
      setSectionPickRowid(null); setPickedSection(null);
      setItemMsg((m) => ({ ...m, [it.rowid]: `部門「${sectionName}」で proposal 化しました — 承認へ進めます` }));
      setToast('proposal化しました(→#invoiceで承認)');
      await loadRuns();
    } catch (e: unknown) {
      // ピッカーは開いたまま、失敗理由をその場に表示 (別部門の選び直し・Claudeへの切替が即できる)
      setResolveErr((m) => ({ ...m, [it.rowid]: `部門指定での再作成に失敗: ${e instanceof Error ? e.message : String(e)}` }));
    } finally { setItemBusy(null); }
  }, [loadRuns]);

  // -- [Claudeに指示]: CommandDock を開き文脈をプレフィル (送信は user が行う。job は既存 user_message 型) --
  const openDockWithContext = useCallback((runId: string | number, it: InvoiceRunItem, fallbackReason: string) => {
    const note = (it.note || '').trim() || fallbackReason || '(理由不明)';
    const text = `【対応待ち(要修正)】run ${runId} / rowid ${it.rowid} ${it.partner_name || '業者不明'} / 理由: ${note}\n指示: `;
    window.dispatchEvent(new CustomEvent('freeeman:dock-prefill', { detail: { text } }));
  }, []);

  const doInstruct = useCallback(async (runId: string | number, it: InvoiceRunItem, text: string) => {
    if (!text.trim()) return;
    setActionErr(null); setItemBusy(it.rowid);
    try {
      // 2026-07-08 jobs基盤へ切替: 旧 POST /api/instructions はミニPC Runner に届かない
      // (runner は /api/runner/claim = jobs のみ)。CommandDock/job-prompt の
      // INVOICE_RUN_RESOLVE_RECIPE が反応する実UIフォーマットで user_message job を発行する。
      await createJob(
        'user_message',
        {
          text: `【対応待ち(要修正)】run ${runId} / rowid ${it.rowid} ${it.partner_name || '業者不明'} / 理由: ${(it.note || '').trim() || '(理由不明)'}\n指示: ${text.trim()}`,
          source: 'invoice_runs_instruct',
          context: { run_id: String(runId), arica_rowid: it.rowid, note_raw: it.note || null },
        },
        { created_by: 'freeeman-web' },
      );
      const p = (x: number) => String(x).padStart(2, '0');
      const d = new Date();
      setSentMap((m) => ({ ...m, [it.rowid]: `${p(d.getHours())}:${p(d.getMinutes())}` }));
      setInstructRowid(null); setInstructText('');
      setToast(`rowid ${it.rowid} の指示を送信しました。AIが順次処理します`);
    } catch (e: unknown) {
      setActionErr(`指示送信失敗: ${e instanceof Error ? e.message : String(e)}`);
    } finally { setItemBusy(null); }
  }, []);

  // ---------------------------------------------------------------------- render
  return (
    <div style={S.page}>
      <style>{'@keyframes irspin{to{transform:rotate(360deg)}}'}</style>
      <Container>
        {/* ===================== ① 新規処理カード (最上部) ===================== */}
        <section style={S.sheet}>
          <header style={S.top}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
              <h1 style={S.topTitle}>請求書処理run</h1>
              <span style={S.topCaption}>前回カーソルの続きから「◯月◯日支払分」をワンクリック一括処理</span>
              <span style={S.pill}><MdOutlineFlag size={13} /> カーソル起点 = 日付範囲の口頭指定は不要</span>
            </div>
            <Button appearance="secondary" small onClick={loadAll} disabled={loading}>
              {loading ? <InlineSpinner isLoading /> : <MdRefresh />} 更新
            </Button>
          </header>

          {actionErr && <div style={{ padding: 12 }}><MessageBlock error>{actionErr}</MessageBlock></div>}

          <div style={{ padding: '14px 18px 18px' }}>
            {/* -------- 開始ガード: 処理中 run がある間は進捗バナー + カードグレーアウト -------- */}
            {guardActive && activeRun && (() => {
              const ac = countsOf(activeRun);
              const aid = String(activeRun.id);
              return (
                <div role="status" style={{
                  display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
                  padding: '12px 14px', marginBottom: 14,
                  border: `1px solid ${C.blueBd}`, borderRadius: 6, background: C.blueBg,
                }}>
                  <MiniSpin size={14} />
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', color: C.blue, fontWeight: 800, fontSize: 13 }}>
                    <span>処理中のrunがあります: <b style={{ fontFamily: NUM }}>{aid}</b></span>
                    <span style={{ fontFamily: NUM, fontVariantNumeric: 'tabular-nums' }}>
                      抽出完了 {Math.min(ac.extractDone, ac.n)}/{ac.n} ・ 完了 {Math.min(ac.done, ac.n)}/{ac.n} ・ あなたの対応待ち {ac.actionRequired}
                    </span>
                    {activeRuns.length > 1 && <span style={{ fontSize: 11 }}>ほか処理中 {activeRuns.length - 1} 件</span>}
                  </div>
                  <Button appearance="secondary" small onClick={() => {
                    window.location.hash = `#invoice_runs/${encodeURIComponent(aid)}`;
                    setTimeout(() => document.getElementById(`run-card-${aid}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 150);
                  }}>詳細を見る</Button>
                  <span style={{ color: C.sub, fontSize: 11, fontWeight: 700 }}>
                    処理中は新規の一括処理開始とプレビュー取得を停止します (10秒毎自動更新・run完了で自動再開)
                  </span>
                </div>
              );
            })()}

            {guardActive && !preview && !previewErr && (
              <div style={{ padding: 18, textAlign: 'center', color: C.sub, fontWeight: 700, border: `1px dashed ${C.lineDark}`, borderRadius: 6 }}>
                プレビュー取得は停止中です。処理中のrunが完了 (close) すると自動的に再開します。
              </div>
            )}

            {previewErr && <MessageBlock error>{previewErr} — worker API が未デプロイの可能性があります。デプロイ後に「更新」を押してください。</MessageBlock>}
            {!guardActive && !previewErr && !preview && <div style={{ padding: 20, color: C.sub, fontWeight: 700 }}><InlineSpinner isLoading /> プレビュー読込中…</div>}

            {preview && (
              <div
                aria-disabled={guardActive || undefined}
                style={guardActive ? { opacity: 0.45, pointerEvents: 'none', userSelect: 'none', filter: 'grayscale(.35)' } : undefined}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap', marginBottom: 10 }}>
                  <span style={S.badge('#f3f8fd', '#476b8e', '#d7e2ef')}>
                    前回カーソル: {preview.cursor != null ? <>rowid <b style={{ fontFamily: NUM }}>{preview.cursor}</b> まで分析済み</> : '未設定 (初回run)'}
                  </span>
                  <span style={S.counter(preview.count > 0 ? C.ink : C.sub)}>未分析 {preview.count}件</span>
                  {excluded.size > 0 && <span style={S.badge(C.warnBg, C.warn, '#e3c68a')}>除外候補チェック中 {excluded.size}件</span>}
                </div>

                {preview.count === 0 && (
                  <div style={{ padding: 22, textAlign: 'center', color: C.sub, fontWeight: 700, border: `1px dashed ${C.lineDark}`, borderRadius: 6 }}>
                    未分析の請求書はありません。新しい請求書が届くとここに現れます。
                  </div>
                )}

                {(preview.items || []).length > 0 && (
                  <div style={{ overflowX: 'auto', maxHeight: 340, overflowY: 'auto', border: `1px solid ${C.lineDark}`, borderRadius: 5 }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', background: '#fff' }}>
                      <thead><tr>
                        <th style={S.th}>除外</th><th style={S.th}>ROWID</th><th style={S.th}>業者</th>
                        <th style={S.th}>受信日</th><th style={{ ...S.th, textAlign: 'right' }}>金額目安</th><th style={S.th}>除外候補理由</th>
                      </tr></thead>
                      <tbody>
                        {(preview.items || []).map((it) => {
                          const isCandidate = !!it.excluded_suggested;
                          const isExcluded = excluded.has(it.rowid);
                          return (
                            <tr key={it.rowid} style={{ background: isCandidate ? C.warnBg : undefined, opacity: isExcluded ? 0.62 : 1 }}>
                              <td style={S.td}>
                                <label style={{ display: 'inline-flex', alignItems: 'center', gap: 5, cursor: 'pointer', fontSize: 11, fontWeight: 800, color: isExcluded ? C.warn : C.sub }}>
                                  <input type="checkbox" checked={isExcluded} onChange={() => toggleExclude(it.rowid)} disabled={guardActive} />
                                  {isExcluded ? '除外' : '含める'}
                                </label>
                              </td>
                              <td style={{ ...S.td, fontFamily: NUM }}>{it.rowid}</td>
                              <td style={{ ...S.td, fontWeight: 700 }}>{it.partner_name || '—'}</td>
                              <td style={{ ...S.td, fontFamily: NUM }}>{it.receive_date || '—'}</td>
                              <td style={{ ...S.td, fontFamily: NUM, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmtYen(it.amount_hint)}</td>
                              <td style={{ ...S.td, color: isCandidate ? C.warn : C.sub, fontSize: 11, maxWidth: 320 }}>
                                {isCandidate ? <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontWeight: 800 }}><MdWarningAmber size={13} /> {it.reason || '除外候補'}</span> : (it.reason || '—')}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}

                {preview.count > 0 && (
                  <div style={{ marginTop: 14, display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
                    <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 12, fontWeight: 800, color: C.sub }}>
                      支払日
                      <input type="date" value={payDate} disabled={guardActive}
                             onChange={(e) => { setPayDate(e.target.value); setConfirming(false); }}
                             style={{ fontFamily: NUM, fontSize: 13, padding: '5px 8px', border: `1px solid ${C.lineDark}`, borderRadius: 4, color: C.ink }} />
                    </label>
                    {!confirming && (
                      skipOnly ? (
                        // 未分析が全件除外 = 抽出対象 0 件。従来はボタンが押せずカーソルが永久に止まったため、
                        // 「除外のみ確定してカーソルを前進」を明示アクションとして出す (2026-09-08)
                        <Button appearance="secondary" onClick={() => setConfirming(true)} disabled={guardActive || creating || !payDate}>
                          <MdPlayArrow /> {excluded.size}件すべてを除外してカーソルを前進 (抽出対象0件)
                        </Button>
                      ) : (
                        <Button appearance="primary" onClick={() => setConfirming(true)} disabled={guardActive || creating || includedCount === 0 || !payDate}>
                          <MdPlayArrow /> {includedCount}件を{payDate ? fmtDateJp(payDate) : '—'}支払分として一括処理開始
                        </Button>
                      )
                    )}
                    {skipOnly && (
                      <span style={{ fontSize: 11, fontWeight: 700, color: C.sub }}>
                        除外チェックを外せば通常の一括処理として開始できます
                      </span>
                    )}
                  </div>
                )}

                {/* 二段階インライン確認 (window.confirm 不使用) */}
                {confirming && !guardActive && preview.count > 0 && (
                  <div style={{ marginTop: 12, padding: '12px 14px', border: `1px solid #e3c68a`, borderRadius: 6, background: C.warnBg }}>
                    <div style={{ fontWeight: 800, marginBottom: 8, color: C.ink }}>
                      {skipOnly ? (
                        <>
                          確認: 未分析 <b style={{ fontFamily: NUM, fontSize: 16 }}>{excluded.size}</b> 件を
                          <b> すべて除外 (スキップ)</b> として記録し、カーソルを
                          <b style={{ fontFamily: NUM }}> rowid {preview.max_rowid ?? '—'}</b> まで前進させます
                          (抽出・proposal 生成は行いません)。
                        </>
                      ) : (
                        <>
                          確認: 対象 <b style={{ fontFamily: NUM, fontSize: 16 }}>{includedCount}</b> 件を
                          <b> {fmtDateJp(payDate)}支払分</b> として一括処理を開始します
                          (除外 <b style={{ fontFamily: NUM }}>{excluded.size}</b> 件はカーソル前進時にスキップ扱い)。
                        </>
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      <Button appearance="primary" small onClick={doCreate} disabled={creating}>
                        {creating ? <InlineSpinner isLoading /> : <MdCheckCircle />} {skipOnly ? '除外して前進する (確定)' : '開始する (確定)'}
                      </Button>
                      <Button appearance="secondary" small onClick={() => setConfirming(false)} disabled={creating}>戻る</Button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </section>

        {/* ===================== ② run 台帳 (一覧 + 詳細) ===================== */}
        <section style={S.sheet}>
          <header style={S.top}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
              <h2 style={S.topTitle}>run台帳</h2>
              <span style={S.topCaption}>各runの抽出・proposal化の進捗を全件追跡 (処理中は10秒毎自動更新)</span>
            </div>
          </header>

          {runsErr && <div style={{ padding: 12 }}><MessageBlock error>{runsErr}</MessageBlock></div>}

          <section style={S.list}>
            {runs.length === 0 && !runsErr && !loading && (
              <div style={{ padding: 30, textAlign: 'center', color: C.sub, fontWeight: 700 }}>
                runはまだありません。上のカードから一括処理を開始すると、ここに進捗が現れます。
              </div>
            )}
            {runs.map((r) => {
              const id = String(r.id);
              const isOpen = detailId === id;
              const c = countsOf(r);
              const [stLabel, stBg, stFg, stBd] = RUN_STATUS[r.status] || [r.status, '#eee', '#333', '#ccc'];
              const allProposed = c.n > 0 && c.proposed >= c.n && c.failed === 0;
              return (
                <div key={id} id={`run-card-${id}`} style={S.card(r.status === 'attention')}>
                  <div style={S.cardHead} onClick={() => { window.location.hash = isOpen ? '#invoice_runs' : `#invoice_runs/${encodeURIComponent(id)}`; }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <b style={{ fontFamily: NUM, fontSize: 13 }}>{id}</b>
                        <span style={S.badge(stBg, stFg, stBd)} title={r.status === 'closed' && r.close_reason ? `close理由: ${r.close_reason}` : undefined}>{stLabel}</span>
                        {r.payment_date && <span style={S.badge('#f3f8fd', '#476b8e', '#d7e2ef')}>{fmtDateJp(r.payment_date)}支払分</span>}
                      </div>
                      <span style={{ color: C.sub, fontSize: 11 }}>
                        {fmtTs(r.created_at)} ／ rowid <span style={{ fontFamily: NUM }}>{r.from_rowid ?? '—'}〜{r.to_rowid ?? '—'}</span>{r.created_by ? ` ／ ${r.created_by}` : ''}
                      </span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
                      <span style={S.counter(c.extractDone >= c.n && c.n > 0 ? C.good : c.failed > 0 ? C.danger : C.ink)}>抽出 {Math.min(c.extractDone, c.n)}/{c.n}</span>
                      <span style={S.counter(allProposed ? C.good : c.failed > 0 ? C.danger : C.warn)}>proposal化 {Math.min(c.proposed, c.n)}/{c.n}</span>
                      {c.actionRequired > 0 && <span style={S.badge(C.warnBg, C.warn, '#e3c68a')} title="内訳: 要修正・処理失敗・開始失敗 — 行を開くと理由と次アクションを表示"><MdWarningAmber size={12} /> あなたの対応待ち {c.actionRequired} (この一覧で対応)</span>}
                      <span style={S.badge('#f1f4f7', '#5e7083', '#ccd6df')}>除外 {c.excluded}</span>
                      {c.failed > 0 && <span style={S.badge(C.dangerBg, C.danger, '#e6b0ac')}><MdWarningAmber size={12} /> 失敗 {c.failed}</span>}
                      {allProposed && <span style={S.badge(C.goodBg, C.good, '#9fdbc7')}><MdCheckCircle size={12} /> 全件proposal化済</span>}
                    </div>
                  </div>

                  {isOpen && detail && String(detail.run.id) === id && (() => {
                    // 「今まさに抽出中」を最上部に固定 (stable sort: 抽出中 → 元の順)
                    const items = detail.items || [];
                    const sortedItems = items
                      .map((it, i) => ({ it, i }))
                      .sort((a, b) => ((isActiveItem(a.it) ? 0 : 1) - (isActiveItem(b.it) ? 0 : 1)) || (a.i - b.i))
                      .map((x) => x.it);
                    const activeItems = sortedItems.filter(isActiveItem);
                    const agg = detailAgg;
                    const pct = (x: number) => (agg && agg.n > 0 ? `${Math.min(100, (x / agg.n) * 100)}%` : '0%');
                    const reasonOf = (it: InvoiceRunItem): ReviewReason =>
                      translateNote(it.note, { state: it.state, rowid: it.rowid, proposalRef: it.proposal_ref, proposalStatus: it.proposal_status });
                    const reviewItems = items.filter((it) => REVIEW_STATES.has(it.state) || FAILED_STATES.has(it.state) || it.state === 'pending');
                    const reasonChips = groupReasons(reviewItems);
                    // E4: まとめて再作成の対象は rebuild_item アクションの item のみ (amount_mismatch/GATE_D 等は含めない)
                    const rebuildTargets = reviewItems.filter((it) => reasonOf(it).action.type === 'rebuild_item').map((it) => it.rowid);
                    const filteredItems = reasonFilter ? sortedItems.filter((it) => reasonOf(it).code === reasonFilter) : sortedItems;
                    const invoiceMonth = detail.run.payment_date ? detail.run.payment_date.slice(0, 7) : null;
                    return (
                    <div style={{ borderTop: `1px solid ${C.lineDark}`, padding: '10px 15px 14px', background: '#fbfcfd' }}>
                      {/* いまの状況ストリップ (2026-07-08: 誰が動くかを明記した4行+バー) */}
                      {agg && (
                        <div style={{ marginBottom: 12, padding: '10px 14px', border: `1px solid ${C.lineDark}`, borderRadius: 6, background: '#fff' }}>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 5, fontSize: 13, fontWeight: 800 }}>
                            <span style={{ color: C.blue }}>
                              ● 処理中 <b style={{ fontFamily: NUM }}>{agg.processing}</b>件 — システムが作業中です。待つだけでOK
                            </span>
                            <span style={{ color: C.warn }}>
                              ● あなたの対応待ち <b style={{ fontFamily: NUM }}>{agg.actionRequired}</b>件
                              {agg.actionRequired > 0 && (
                                <span style={{ fontWeight: 700 }}>
                                  {' — '}
                                  {agg.dupReview > 0 && <>重複・却下の判定 <b style={{ fontFamily: NUM }}>{agg.dupReview}</b>件(<a href="#duplicate_candidates" style={{ color: C.warn }}>重複疑いページ</a>)・</>}
                                  エラー対応 <b style={{ fontFamily: NUM }}>{agg.actionRequired - agg.dupReview}</b>件(下の表の「操作」列で)
                                </span>
                              )}
                            </span>
                            <span style={{ color: C.good }}>
                              ● 完了 <b style={{ fontFamily: NUM }}>{agg.done}</b>件
                              {agg.done > 0 && invoiceMonth && (
                                <a href={`#invoice/${invoiceMonth}`} style={{ color: C.good, marginLeft: 8 }}>→ 請求書処理で承認する</a>
                              )}
                            </span>
                            <span style={{ color: C.sub }}>● 除外 <b style={{ fontFamily: NUM }}>{Math.max(items.length - agg.n, 0)}</b>件</span>
                          </div>
                          <div aria-hidden style={{ display: 'flex', height: 10, borderRadius: 5, overflow: 'hidden', border: `1px solid ${C.lineDark}`, background: '#eef2f6', marginTop: 8 }}>
                            <div style={{ width: pct(agg.done), background: C.good, transition: 'width .4s' }} />
                            <div style={{ width: pct(agg.actionRequired), background: '#d99a2b', transition: 'width .4s' }} />
                            <div style={{ width: pct(agg.processing), background: C.blue, transition: 'width .4s' }} />
                          </div>
                          <div style={{ marginTop: 4, fontFamily: NUM, fontSize: 12, fontWeight: 800, color: C.sub, fontVariantNumeric: 'tabular-nums' }}>
                            {Math.min(agg.done, agg.n)}/{agg.n} 完了
                          </div>
                          {r.status === 'attention' && agg.processing > 0 && (
                            <div style={{ marginTop: 6, color: C.danger, fontSize: 12, fontWeight: 800 }}>
                              <MdWarningAmber size={13} /> 30分以上進捗がありません。処理役 (Runner) が動いているか確認してください。
                            </div>
                          )}
                          {jobsProbeFailed && (
                            <div style={{ marginTop: 6, color: C.sub, fontSize: 11, fontWeight: 700 }}>
                              処理中判定が取得できていません (表示上止まって見える場合があります)。
                            </div>
                          )}
                          {reasonChips.length > 0 && (
                            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
                              {reasonChips.map((ch) => (
                                <button key={ch.code} type="button"
                                        onClick={() => setReasonFilter(reasonFilter === ch.code ? null : ch.code)}
                                        style={{
                                          ...S.badge(reasonFilter === ch.code ? C.warn : C.warnBg, reasonFilter === ch.code ? '#fff' : C.warn, '#e3c68a'),
                                          cursor: 'pointer', fontSize: 11,
                                        }}>
                                  {ch.title} {ch.count}
                                </button>
                              ))}
                              {reasonFilter && (
                                <button type="button" onClick={() => setReasonFilter(null)}
                                        style={{ ...S.badge('#f1f4f7', '#5e7083', '#ccd6df'), cursor: 'pointer', fontSize: 11 }}>
                                  絞り込み解除
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      )}

                      {/* 今まさに処理中 (job_status claimed/running) の固定表示 */}
                      {activeItems.length > 0 && (
                        <div role="status" style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', padding: '8px 12px', marginBottom: 10, borderRadius: 5, background: C.blueBg, border: `1px solid ${C.blueBd}`, color: C.blue, fontSize: 12, fontWeight: 800 }}>
                          <MiniSpin /> 現在処理中:
                          {activeItems.map((it) => (
                            <span key={it.rowid} style={{ fontFamily: NUM, fontVariantNumeric: 'tabular-nums' }}>
                              rowid {it.rowid} <span style={{ fontFamily: FONT }}>{it.partner_name || '—'}</span>
                            </span>
                          ))}
                        </div>
                      )}

                      <div style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap', alignItems: 'center' }}>
                        {agg && (agg.failed > 0 || (agg.actionRequired - agg.review - agg.failed) > 0) && (
                          <Button appearance="primary" small onClick={() => doRedispatch(r.id)}>失敗・未開始分をまとめて再実行</Button>
                        )}
                        {rebuildTargets.length > 0 && (
                          <Button appearance="primary" small onClick={() => doRebuild(r.id, rebuildTargets)} disabled={rebuildingId != null}>
                            {rebuildingId === id ? <InlineSpinner isLoading /> : null} エラー対応分をまとめて再作成 ({rebuildTargets.length}件)
                          </Button>
                        )}
                        {r.status !== 'closed' && closingId !== id && (
                          <Button appearance="secondary" small onClick={() => { setClosingId(id); setCloseReason(''); }}>
                            runを完了 (カーソル前進)
                          </Button>
                        )}
                      </div>

                      {/* close の二段階インライン確認 */}
                      {closingId === id && r.status !== 'closed' && (
                        <div style={{ marginBottom: 10, padding: '10px 12px', border: '1px solid #e3c68a', borderRadius: 6, background: C.warnBg }}>
                          <div style={{ fontWeight: 800, marginBottom: 6 }}>
                            確認: このrunを完了し、カーソルを rowid <b style={{ fontFamily: NUM }}>{r.to_rowid ?? '—'}</b> へ前進させます。
                            {!allProposed && <span style={{ color: C.warn }}> (全件proposal化済ではありません — 未完了分を残したままcloseする場合は理由を残してください)</span>}
                            {agg && agg.actionRequired > 0 && (
                              <span style={{ color: C.warn }}> あなたの対応待ちが <b style={{ fontFamily: NUM }}>{agg.actionRequired}</b> 件残っています。closeしても消えません (runは監査記録として残ります)。</span>
                            )}
                          </div>
                          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                            <input type="text" value={closeReason} onChange={(e) => setCloseReason(e.target.value)}
                                   placeholder={allProposed ? '理由 (任意)' : 'closeする理由 (監査記録に残ります)'}
                                   style={{ flex: '1 1 260px', fontSize: 12, padding: '5px 8px', border: `1px solid ${C.lineDark}`, borderRadius: 4 }} />
                            <Button appearance="primary" small onClick={() => doClose(r)} disabled={!allProposed && !closeReason.trim()}>完了する (確定)</Button>
                            <Button appearance="secondary" small onClick={() => setClosingId(null)}>戻る</Button>
                          </div>
                        </div>
                      )}

                      <div style={{ overflowX: 'auto', border: `1px solid ${C.lineDark}`, borderRadius: 5 }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', background: '#fff' }}>
                          <thead><tr>
                            <th style={S.th}>ROWID</th><th style={S.th}>業者</th><th style={S.th}>受信日</th>
                            <th style={S.th}>状態</th><th style={S.th}>状況と次の一手</th><th style={S.th}>操作</th>
                          </tr></thead>
                          <tbody>
                            {filteredItems.map((it) => {
                              const active = isActiveItem(it);
                              const sent = sentMap[it.rowid];
                              // resolved = 部門指定 resolve-item 成功の楽観表示 (server refresh で state=proposed に追いつくまでの橋渡し)
                              const resolved = resolvedRowids.has(it.rowid);
                              const badge = active
                                ? { bg: C.blueBg, fg: C.blue, bd: C.blueBd, label: '処理中(読取中)', status: 'processing' as const }
                                : resolved
                                  ? runItemToUnified('proposed', null)
                                  : sent
                                    ? { bg: C.blueBg, fg: C.blue, bd: C.blueBd, label: '処理中(指示対応中)', status: 'processing' as const }
                                    : runItemToUnified(it.state, it.note);
                              const reason = reasonOf(it);
                              const isReview = REVIEW_STATES.has(it.state) && !resolved;
                              const isFailed = FAILED_STATES.has(it.state) && !resolved;
                              // section_unresolved 系 (RULE_7 含む) = 部門ピッカーの数秒操作で解消できる行
                              const isSectionIssue = isReview && (reason.code === 'section' || /section_unresolved|RULE_7/.test(String(it.note || '')));
                              const sevColor = reason.severity === 'error' ? C.danger : reason.severity === 'warn' ? C.warn : reason.severity === 'ok' ? C.good : C.sub;
                              const busy = itemBusy === it.rowid;
                              const showAction = !active && !sent && !resolved && badge.status !== 'processing';
                              return (
                                <React.Fragment key={it.rowid}>
                                <tr style={{ background: active ? C.blueBg : isReview ? C.warnBg : undefined }}>
                                  <td style={{ ...S.td, fontFamily: NUM }}>{it.rowid}</td>
                                  <td style={{ ...S.td, fontWeight: 700 }}>{it.partner_name || '—'}</td>
                                  <td style={{ ...S.td, fontFamily: NUM }}>{it.receive_date || '—'}</td>
                                  <td style={S.td}>
                                    <span style={S.badge(badge.bg, badge.fg, badge.bd)}>
                                      {(active || !!sent) && <MiniSpin size={9} />}
                                      {!active && !sent && badge.status === 'done' && <MdCheckCircle size={11} />}
                                      {!active && !sent && badge.status === 'action_required' && <MdWarningAmber size={11} />}
                                      {badge.label}
                                    </span>
                                  </td>
                                  <td style={{ ...S.td, fontSize: 11, maxWidth: 360 }} title={(it.note || '').trim() || undefined}>
                                    {reason.action.type === 'wait' || reason.action.type === 'none' ? (
                                      <span style={{ color: C.sub }}>{reason.detail}</span>
                                    ) : (
                                      <>
                                        <div style={{ fontWeight: 800, color: sevColor }}>{reason.title}</div>
                                        <div style={{ color: C.sub, marginTop: 2 }}>{reason.detail}</div>
                                        {reason.autoRetried && (
                                          <span style={{ ...S.badge('#f1f4f7', '#5e7083', '#ccd6df'), marginTop: 4 }}>自動再試行済み</span>
                                        )}
                                        {itemMsg[it.rowid] && (
                                          <div style={{ marginTop: 4, fontWeight: 800, color: C.blue }}>{itemMsg[it.rowid]}</div>
                                        )}
                                        {reason.raw && (
                                          <details style={{ marginTop: 4 }}>
                                            <summary style={{ cursor: 'pointer', color: C.sub, fontSize: 10 }}>技術情報</summary>
                                            <code style={{ fontFamily: NUM, fontSize: 10, color: C.sub, wordBreak: 'break-all' }}>{reason.raw}</code>
                                          </details>
                                        )}
                                      </>
                                    )}
                                  </td>
                                  <td style={{ ...S.td, whiteSpace: 'nowrap' }}>
                                    {sent && <span style={{ fontSize: 11, fontWeight: 800, color: C.blue }}>指示送信済 {sent}</span>}
                                    {/* 部門未確定はピッカーの数秒操作が最速経路 (Claude経由なし) — 2026-07-08 */}
                                    {showAction && isSectionIssue && (
                                      <div style={{ marginBottom: 5 }}>
                                        <Button appearance="primary" small disabled={busy || r.status === 'closed'}
                                                onClick={() => openSectionPicker(it.rowid)}>
                                          {sectionPickRowid === it.rowid ? '部門ピッカーを閉じる ▲' : '部門を指定して再作成 ▼'}
                                        </Button>
                                      </div>
                                    )}
                                    {showAction && reason.action.type === 'approve_link' && invoiceMonth && (
                                      <a href={`#invoice/${invoiceMonth}?ref=arica:${it.rowid}`} style={{ color: '#476b8e', fontWeight: 800, fontSize: 12 }}>承認画面へ ↗</a>
                                    )}
                                    {showAction && reason.action.type === 'dup_link' && (
                                      <a href="#duplicate_candidates" style={{ color: C.warn, fontWeight: 800, fontSize: 12 }}>重複判定画面へ ↗</a>
                                    )}
                                    {showAction && reason.action.type === 'rebuild_item' && (
                                      <Button appearance="secondary" small disabled={busy || r.status === 'closed'} onClick={() => doRebuildItem(r.id, it.rowid)}>
                                        {busy ? <MiniSpin size={9} /> : null} {reason.action.label || '再作成'}
                                      </Button>
                                    )}
                                    {showAction && reason.action.type === 'redispatch' && (
                                      <Button appearance="secondary" small disabled={busy || r.status === 'closed'} onClick={() => doRedispatchItem(r.id, it.rowid)}>
                                        {busy ? <MiniSpin size={9} /> : null} 再実行
                                      </Button>
                                    )}
                                    {showAction && reason.action.type === 'instruct' && (
                                      <Button appearance="secondary" small disabled={busy} onClick={() => {
                                        setInstructRowid(instructRowid === it.rowid ? null : it.rowid);
                                        setInstructText(reason.action.instructionPrefill || '');
                                      }}>
                                        {reason.action.label || 'AIに指示'}
                                      </Button>
                                    )}
                                    {/* 対応待ち(要修正)/失敗行: 人が請求書を実際に閲覧 → Claudeに文脈付きで指示する動線 (2026-07-08)
                                        請求書を見る = 行直下にインライン展開 (仕訳検証ページと同じ PdfViewer。再クリック/別行クリックで閉じ・切替)
                                        Claudeに指示 = 複雑な指示(自由文)用。部門指定だけなら上のピッカーが数秒で完了 (ヒント文で誘導) */}
                                    {(isReview || isFailed) && (
                                      <>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 5 }}>
                                          <button type="button"
                                                  onClick={() => setViewerRowid(viewerRowid === it.rowid ? null : it.rowid)}
                                                  title={viewerRowid === it.rowid ? 'プレビューを閉じる' : `請求書PDFをこの画面で見る (rowid ${it.rowid})`}
                                                  aria-expanded={viewerRowid === it.rowid}
                                                  style={{
                                                    padding: '3px 8px', borderRadius: 4, cursor: 'pointer',
                                                    border: `1px solid ${viewerRowid === it.rowid ? C.blueBd : 'transparent'}`,
                                                    background: viewerRowid === it.rowid ? C.blueBg : 'none',
                                                    color: '#476b8e', fontWeight: 800, fontSize: 12, whiteSpace: 'nowrap', fontFamily: FONT,
                                                  }}>
                                            {viewerRowid === it.rowid ? '請求書を閉じる ▲' : '請求書を見る ▼'}
                                          </button>
                                          <Button appearance="secondary" small onClick={() => openDockWithContext(r.id, it, reason.title)}>
                                            Claudeに指示 (複雑な指示・自由文)
                                          </Button>
                                        </div>
                                        {isSectionIssue && (
                                          <div style={{ marginTop: 3, fontSize: 10, fontWeight: 700, color: C.sub, whiteSpace: 'normal', maxWidth: 260 }}>
                                            部門の指定だけなら [部門を指定して再作成] が数秒で完了します (Claude不要)
                                          </div>
                                        )}
                                      </>
                                    )}
                                  </td>
                                </tr>
                                {/* インライン請求書ビューア (行直下・テーブル幅いっぱい・~70vh。
                                    読込中スピナー/pdf.js⇄画像fallback/頁送りは PdfViewer 側が仕訳検証ページと同一挙動で処理) */}
                                {viewerRowid === it.rowid && (
                                  <tr id={`invoice-viewer-row-${it.rowid}`}>
                                    <td colSpan={6} style={{ padding: 0, borderBottom: `1px solid ${C.lineDark}`, background: '#f4f7fa' }}>
                                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap', padding: '7px 12px', borderBottom: `1px solid ${C.line}` }}>
                                        <span style={{ fontSize: 12, fontWeight: 800, color: C.sub }}>
                                          請求書プレビュー — rowid <span style={{ fontFamily: NUM }}>{it.rowid}</span> {it.partner_name || ''}
                                          <span style={{ fontWeight: 700, marginLeft: 8 }}>(開いたまま「Claudeに指示」や右下のチャットで指示できます)</span>
                                        </span>
                                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
                                          <a href={invoicePdfUrl(it.rowid)} target="_blank" rel="noopener noreferrer"
                                             style={{ color: '#476b8e', fontWeight: 800, fontSize: 12, whiteSpace: 'nowrap' }}>
                                            新規タブで開く ↗
                                          </a>
                                          <Button appearance="secondary" small onClick={() => setViewerRowid(null)}>閉じる</Button>
                                        </span>
                                      </div>
                                      {/* 高さはラッパ側で確保 (堅牢化: PdfViewer のどの branch でも 70vh/最低400px を保証。
                                          小さい窓でも Viewer コンテナが 0 高で潰れて IntersectionObserver 不発 → 空白、を防ぐ) */}
                                      <div style={{ height: '70vh', minHeight: 400 }}>
                                        <PdfViewer url={invoicePdfUrl(it.rowid)} title={it.partner_name || `rowid ${it.rowid}`} height="100%" />
                                      </div>
                                    </td>
                                  </tr>
                                )}
                                {/* 部門ピッカー (行直下展開・同時1件): freee部門canonical一覧から単一選択 → 全明細行に適用して再作成 */}
                                {sectionPickRowid === it.rowid && (
                                  <tr>
                                    <td colSpan={6} style={{ ...S.td, background: '#f4f7fa' }}>
                                      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                        <span style={{ fontSize: 11, fontWeight: 800, color: C.sub }}>
                                          部門(店舗)を1つ選んでください — 選んだ部門を全明細行に適用し、そのまま仕訳の下書き (proposal) を作成します
                                        </span>
                                        {sectionOptionsErr && <MessageBlock error>{sectionOptionsErr}</MessageBlock>}
                                        {!sectionOptions && !sectionOptionsErr && (
                                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: C.sub, fontSize: 12, fontWeight: 700 }}>
                                            <MiniSpin /> 部門一覧を読込中… (freee部門マスタ)
                                          </span>
                                        )}
                                        {sectionOptions && sectionOptions.length === 0 && (
                                          <MessageBlock error>部門一覧が空です。freee部門マスタを確認してください。</MessageBlock>
                                        )}
                                        {sectionOptions && sectionOptions.length > 0 && (
                                          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                                            {sectionOptions.map((sec) => {
                                              const on = pickedSection === sec.id;
                                              return (
                                                <button key={sec.id} type="button" disabled={busy}
                                                        aria-pressed={on}
                                                        onClick={() => setPickedSection(on ? null : sec.id)}
                                                        style={{
                                                          padding: '4px 10px', borderRadius: 13, cursor: busy ? 'default' : 'pointer',
                                                          border: `1px solid ${on ? C.navy : C.lineDark}`,
                                                          background: on ? C.navy : '#fff', color: on ? '#fff' : C.ink,
                                                          fontSize: 12, fontWeight: 800, fontFamily: FONT, whiteSpace: 'nowrap',
                                                        }}>
                                                  {sec.name}
                                                </button>
                                              );
                                            })}
                                          </div>
                                        )}
                                        {resolveErr[it.rowid] && (
                                          <div style={{ color: C.danger, fontSize: 12, fontWeight: 800 }}>
                                            <MdWarningAmber size={13} /> {resolveErr[it.rowid]} — 別の部門で再試行するか、[Claudeに指示] で依頼してください
                                          </div>
                                        )}
                                        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                                          <Button appearance="primary" small
                                                  disabled={busy || pickedSection == null || r.status === 'closed'}
                                                  onClick={() => {
                                                    const sec = (sectionOptions || []).find((s) => s.id === pickedSection);
                                                    if (sec) doResolveSection(r.id, it, sec.id, sec.name);
                                                  }}>
                                            {busy ? <MiniSpin size={9} /> : null} この部門で再作成
                                          </Button>
                                          <Button appearance="secondary" small disabled={busy}
                                                  onClick={() => { setSectionPickRowid(null); setPickedSection(null); }}>
                                            閉じる
                                          </Button>
                                          {pickedSection != null && (
                                            <span style={{ fontSize: 11, fontWeight: 800, color: C.navy }}>
                                              選択中: {(sectionOptions || []).find((s) => s.id === pickedSection)?.name || pickedSection}
                                            </span>
                                          )}
                                        </div>
                                      </div>
                                    </td>
                                  </tr>
                                )}
                                {instructRowid === it.rowid && (
                                  <tr>
                                    <td colSpan={6} style={{ ...S.td, background: '#f4f7fa' }}>
                                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                        <span style={{ fontSize: 11, fontWeight: 800, color: C.sub }}>
                                          AIへの指示 (内容は編集できます。送信するとAIが順次処理します)
                                        </span>
                                        <textarea value={instructText} onChange={(e) => setInstructText(e.target.value)} rows={3}
                                                  style={{ width: '100%', boxSizing: 'border-box', fontFamily: FONT, fontSize: 12, padding: '6px 9px', border: `1px solid ${C.lineDark}`, borderRadius: 4, resize: 'vertical' }} />
                                        <div style={{ display: 'flex', gap: 8 }}>
                                          <Button appearance="primary" small disabled={busy || !instructText.trim()} onClick={() => doInstruct(r.id, it, instructText)}>
                                            {busy ? <MiniSpin size={9} /> : null} 送信
                                          </Button>
                                          <Button appearance="secondary" small disabled={busy} onClick={() => { setInstructRowid(null); setInstructText(''); }}>閉じる</Button>
                                        </div>
                                      </div>
                                    </td>
                                  </tr>
                                )}
                                </React.Fragment>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>

                      {/* ③ フロー接続の案内 */}
                      <div style={{ ...S.flowHint, marginTop: 10 }}>
                        <span>run完了後の動線:</span>
                        <span>完了(→承認へ)の件は <a href="#invoice" style={{ color: '#33699e', fontWeight: 800 }}>請求書処理(#invoice)</a> で承認 → 一括登録</span>
                        <span>(登録後は <a href="#batches" style={{ color: '#33699e', fontWeight: 800 }}>バッチ台帳(#batches)</a> で全件照合)</span>
                        <span>エラー対応分はこの表の「操作」列から個別に対応できます</span>
                      </div>
                    </div>
                    );
                  })()}
                </div>
              );
            })}
          </section>
        </section>
      </Container>

      {/* toast (close成功「カーソルが rowid X に前進しました」・「再build: 成功n・解決不能k」等) */}
      {toast && (
        <div role="status" style={{
          position: 'fixed', right: 20, bottom: 20, zIndex: 1200,
          display: 'flex', alignItems: 'center', gap: 8,
          padding: '10px 16px', borderRadius: 7, border: '1px solid #9fdbc7',
          background: C.goodBg, color: C.good, fontWeight: 800, fontSize: 13,
          boxShadow: '0 9px 22px rgba(33,52,74,.18)', fontFamily: FONT,
        }}>
          <MdCheckCircle size={16} /> {toast}
        </div>
      )}
    </div>
  );
};

export default InvoiceRunsPage;
